import React from 'react';
import { Address } from './types';
import { 
  PagerBar, 
  SearchListBar,
  SearchCriterion,
  SearchableField,
  SortBar,
  SortOption,
  SortColumn,
  ListItemSection,
  LoadingBar,
  formatAddress,
  ListItemDisplayData
} from '../../../components/common';
import '../../../components/common/CRMList.css';

interface AddressListProps {
  addresses: Address[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  selectedAddressId?: number;
  onSelectAddress: (address: Address) => void;
  onDeleteAddress: (address: Address) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onCreateNew: () => void;
  onSearch?: (searchParams: any) => void;
  globalLoading: boolean;
  loadingMessage: string;
}

const AddressList: React.FC<AddressListProps> = ({
  addresses,
  loading,
  error,
  pagination,
  selectedAddressId,
  onSelectAddress,
  onDeleteAddress,
  onPageChange,
  onLimitChange,
  onCreateNew,
  onSearch,
  globalLoading,
  loadingMessage
}) => {
  // Search state for search and sort functionality
  const [searchValue, setSearchValue] = React.useState('');
  const [selectedCriteria, setSelectedCriteria] = React.useState<SearchCriterion[]>([]);
  const [currentSorts, setCurrentSorts] = React.useState<SortOption[]>([
    {
      field: 'street1',
      direction: 'asc',
      label: 'Street Address'
    }
  ]);
  
  // Searchable fields configuration for SearchListBar
  const searchableFields: SearchableField[] = [
    { key: 'street1', label: 'Street Address', type: 'text' },
    { key: 'city', label: 'City', type: 'text' },
    { key: 'state', label: 'State', type: 'text' },
    { key: 'zip', label: 'Zip Code', type: 'text' },
    { key: 'country', label: 'Country', type: 'text' }
  ];
  
  // Sort column configuration - must match server valid sort fields
  const sortColumns: SortColumn[] = [
    { field: 'street1', label: 'Street Address' },
    { field: 'city', label: 'City' },
    { field: 'state', label: 'State' },
    { field: 'zip', label: 'Zip Code' },
    { field: 'dateCreated', label: 'Date Created' },
    { field: 'dateUpdated', label: 'Date Updated' }
  ];
  
  // Handle search value changes (debounced)
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (onSearch) {
        if (selectedCriteria.length > 0) {
          // Multiple field search (OR logic)
          onSearch({
            search: searchValue,
            fields: selectedCriteria.map(c => c.field),
            page: 1 // Reset to first page on search
          });
        } else {
          // Global search across all fields
          onSearch({
            search: searchValue,
            fields: undefined,
            page: 1 // Reset to first page on search
          });
        }
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchValue, selectedCriteria]);
  
  // Handle criteria changes - only update state, let useEffect handle search
  const handleCriteriaChange = React.useCallback((criteria: SearchCriterion[]) => {
    setSelectedCriteria(criteria);
  }, []);
  
  // Handle sort changes
  const handleSortsChange = React.useCallback((sorts: SortOption[]) => {
    setCurrentSorts(sorts);
    
    if (onSearch) {
      if (sorts.length > 0) {
        // Convert sorts to the new format
        const sortArray = sorts.map(sort => ({
          field: sort.field as 'street1' | 'city' | 'state' | 'zip' | 'dateCreated' | 'dateUpdated',
          direction: sort.direction as 'asc' | 'desc'
        }));
        
        onSearch({
          sorts: sortArray,
          page: 1 // Reset to first page when sorting changes
        });
      } else {
        // Clear sorts if none selected
        onSearch({
          sorts: undefined,
          page: 1
        });
      }
    }
  }, [onSearch]);
  
  // Helper to check if address is verified (handles both boolean and number as fallback)
  const isVerified = (address: Address): boolean => {
    const verified = address.verified;
    // After API conversion, verified should be boolean, but handle number as fallback
    if (typeof verified === 'boolean') {
      return verified;
    }
    // Fallback: handle number (0 or 1) in case conversion didn't happen
    if (typeof verified === 'number') {
      return verified === 1;
    }
    // Fallback: handle string
    if (typeof verified === 'string') {
      return verified === '1';
    }
    return false;
  };

  // Unified display data function for AddressList
  const getDisplayData = (address: Address): ListItemDisplayData => {
    const addressParts: string[] = [];
    if (address.street1) addressParts.push(address.street1);
    if (address.street2) addressParts.push(address.street2);
    if (address.unit) addressParts.push(`Unit ${address.unit}`);
    const cityStateZip = `${address.city}, ${address.state} ${address.zip}`;
    addressParts.push(cityStateZip);
    
    return {
      title: addressParts.join(', '),
      category: isVerified(address) ? 'Verified' : 'Unverified',
      fields: [
        {
          name: 'location',
          value: `${address.city}, ${address.state} ${address.zip}`
        }
      ]
    };
  };

  if (error) {
    return <div className="app-error-message">{error}</div>;
  }

  return (
    <div className="list">
      <div className="list-header">
        <h3>Addresses ({pagination.total})</h3>
        <button
          className="app-btn app-btn-primary list-btn-add-item"
          onClick={onCreateNew}
          title="Add New Address"
        >
          +
        </button>
      </div>
      
      {/* Search Bar */}
      <SearchListBar
        searchableFields={searchableFields}
        searchValue={searchValue}
        selectedCriteria={selectedCriteria}
        onSearchChange={setSearchValue}
        onCriteriaChange={handleCriteriaChange}
        placeholder="Search addresses..."
      />
      
      {/* Sort Bar */}
      <SortBar
        availableColumns={sortColumns}
        currentSorts={currentSorts}
        onSortsChange={handleSortsChange}
        defaultSort={{
          field: 'street1',
          direction: 'asc',
          label: 'Street Address'
        }}
      />

      <ListItemSection
        items={addresses}
        selectedItemId={selectedAddressId}
        onSelectItem={onSelectAddress}
        onDeleteItem={onDeleteAddress}
        getInUseCount={(address) => address.inUseCount} // Get count of associations
        deleteConfirmMessage={(address) => {
          const addressStr = formatAddress(address);
          return `Are you sure you want to delete ${addressStr}? This action cannot be undone.`;
        }}
        loading={false}
        loadingMessage=""
        emptyMessage="No addresses found. Try adjusting your search criteria."
        getDisplayData={getDisplayData}
      />

      {/* Bottom PagerBar */}
      <PagerBar
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        itemsPerPage={pagination.limit}
        onPageChange={onPageChange}
        onItemsPerPageChange={onLimitChange}
      />
      
      <LoadingBar isLoading={globalLoading} message={loadingMessage} />
    </div>
  );
};

export default AddressList;

